<template>
  <section class="testimonial-section">
    <div class="testimonial-section__container">
      <CardTitle v-if="!hideTitle" :module="item" />

      <Swiper class="testimonial__swiper" :modules="modules" :slides-per-view="1" :space-between="16" :loop="true"
        :autoplay="{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        }" :pagination="{
          clickable: true
        }" :navigation="true" :breakpoints="{
          768: {
            slidesPerView: 3,
            spaceBetween: 20
          }
        }">
        <SwiperSlide v-for="(cell, idx) in list" :key="genKey(cell, idx)">
          <article class="testimonial__card">
            <div class="testimonial__head">
              <div class="testimonial__avatar">
                <img v-if="getCellAvatar(cell)" :src="getCellAvatar(cell)" :alt="getCellName(cell)" loading="lazy" />
                <span v-else class="testimonial__avatar-fallback">{{ getAvatarFallback(cell) }}</span>
              </div>
              <h4 v-if="getCellName(cell)" class="testimonial__name">{{ getCellName(cell) }}</h4>
            </div>
            <p v-if="getCellContent(cell)" class="testimonial__quote">{{ getCellContent(cell) }}</p>
          </article>
        </SwiperSlide>
      </Swiper>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Module } from '@/api/page_config'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord } from './index.js'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

const modules = [Navigation, Pagination, Autoplay]

const props = withDefaults(defineProps<{
  item: Module
  hideTitle?: boolean
}>(), { hideTitle: false })

const list = computed(() => props.item.fieldData || [])

function genKey(cell: unknown, idx: number): string {
  const c = cell as Record<string, unknown>
  return `tm-${idx}-${String(c?.id ?? '')}`
}

function getCellAvatar(cell: unknown): string {
  return getStrWithRecord(cell, 'avatar') || getStrWithRecord(cell, 'image')
}
function getCellName(cell: unknown): string {
  return getStrWithRecord(cell, 'name') || getStrWithRecord(cell, 'title')
}
function getCellContent(cell: unknown): string {
  return getStrWithRecord(cell, 'content') || getStrWithRecord(cell, 'subTitle') || getStrWithRecord(cell, 'desc')
}
function getAvatarFallback(cell: unknown): string {
  const name = getCellName(cell)
  return name ? name.charAt(0).toUpperCase() : '?'
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.testimonial-section {
  width: 100%;
}

.testimonial-section__container {
  max-width: $container-max-width;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: $breakpoint-md) {
    padding: 0 2rem;
  }
}

.testimonial__swiper {
  padding: 1rem 0;
  position: relative;

  :deep(.swiper-wrapper) {
    align-items: stretch;
  }

  :deep(.swiper-slide) {
    height: auto;
  }
}

.testimonial__card {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 1.75rem 1.25rem;
  background: #eeeff0;
  border-radius: 1.25rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;

  @media (min-width: $breakpoint-md) {
    padding: 2.25rem 1.75rem;
    border-radius: 1.5rem;
    box-shadow: none;
  }
}

.testimonial__head {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.testimonial__avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #ddd;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (min-width: $breakpoint-md) {
    width: 72px;
    height: 72px;
  }
}

.testimonial__avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ccc, #aaa);
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
}

.testimonial__name {
  font-size: 1.25rem;
  font-weight: 800;
  color: $color-text;
  margin: 0;

  @media (min-width: $breakpoint-md) {
    font-size: 1.75rem;
  }
}

.testimonial__quote {
  font-size: 1rem;
  color: #777;
  margin: 0;
  line-height: 1.7;
  font-weight: 500;

  @media (min-width: $breakpoint-md) {
    font-size: 1.25rem;
    line-height: 1.8;
    color: #888;
  }
}

/* Navigation arrows - Swiper default styled */
.testimonial__swiper {

  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    display: none;
    width: 40px;
    height: 40px;
    background: #3a3a3a;
    border-radius: 50%;
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease;

    @media (min-width: $breakpoint-md) {
      width: 52px;
      height: 52px;
    }

    &::after {
      font-size: 14px;
      font-weight: 700;

      @media (min-width: $breakpoint-md) {
        font-size: 18px;
      }
    }

    &:hover {
      background: #000;
    }
  }

  :deep(.swiper-button-prev) {
    left: 1rem;
  }

  :deep(.swiper-button-next) {
    right: 1rem;
  }

  :deep(.swiper-navigation-icon) {
    font-size: 1rem;
    height: 1rem;
    width: 1rem;
  }

  :deep(.swiper-button-disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Pagination dots - Swiper default styled */
.testimonial__swiper {
  :deep(.swiper-pagination) {
    position: relative;
    bottom: auto;
    margin-top: 1.5rem;
  }

  :deep(.swiper-pagination-bullet) {
    width: 10px;
    height: 10px;
    background: #d0d0d0;
    opacity: 1;
    transition: all 0.25s ease;
    margin: 0 4px;

    &:hover {
      background: #888;
    }
  }

  :deep(.swiper-pagination-bullet-active) {
    background: #444;
    transform: scale(1.2);
  }
}

@media (min-width: $breakpoint-md) {
  .testimonial__swiper {

    :deep(.swiper-button-prev),
    :deep(.swiper-button-next) {
      display: flex;
    }
  }
}
</style>
